/**
 * File validation utilities
 */

/**
 * Validates file type and size
 * @param {File} file - The file to validate
 * @param {Array<string>} allowedTypes - Array of allowed MIME types
 * @param {number} maxSizeMB - Maximum file size in MB (default: 5)
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export const validateFileType = (file, allowedTypes, maxSizeMB = 5) => {
  if (!file) {
    return { isValid: false, error: "No file provided" };
  }

  // Check file size (convert MB to bytes)
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: `File size must be less than ${maxSizeMB}MB. Current size: ${formatFileSize(file.size)}`,
    };
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `Invalid file type. Allowed types: ${allowedTypes.join(", ")}. Received: ${file.type}`,
    };
  }

  return { isValid: true, error: null };
};

/**
 * Formats file size to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

/**
 * Validates multiple files
 * @param {Array<File>} files - Array of files to validate
 * @param {Array<string>} allowedTypes - Array of allowed MIME types
 * @param {number} maxSizeMB - Maximum file size in MB
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 */
export const validateMultipleFiles = (files, allowedTypes, maxSizeMB = 5) => {
  const errors = [];
  
  files.forEach((file, index) => {
    const validation = validateFileType(file, allowedTypes, maxSizeMB);
    if (!validation.isValid) {
      errors.push(`File ${index + 1}: ${validation.error}`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Gets file extension from filename
 * @param {string} filename - The filename
 * @returns {string} - File extension (e.g., "pdf")
 */
export const getFileExtension = (filename) => {
  if (!filename) return '';
  return filename.split('.').pop().toLowerCase();
};

/**
 * Checks if file is an image
 * @param {File} file - The file to check
 * @returns {boolean} - True if file is an image
 */
export const isImageFile = (file) => {
  return file && file.type.startsWith('image/');
};

/**
 * Checks if file is a PDF
 * @param {File} file - The file to check
 * @returns {boolean} - True if file is a PDF
 */
export const isPDFFile = (file) => {
  return file && file.type === 'application/pdf';
};
